import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';

import ListFilterToolbar from 'src/common/components/list-filters/ListFilterToolbar';
import { TechnicalTypeSortBy } from 'src/helpers/filterTechnicalTypes';
import { hasActiveSelectFilter } from 'src/helpers/listFilters';
import { PublishedFilter } from 'src/helpers/publishedFilter';
import { EOrder } from 'src/interfaces/Order';
import { EView } from 'src/interfaces/View';

interface Props {
  query: string;
  setQuery: (value: string) => void;
  publishedFilter: PublishedFilter;
  setPublishedFilter: (value: PublishedFilter) => void;
  order: EOrder;
  orderByKey: TechnicalTypeSortBy;
  setOrder: (order: EOrder) => void;
  setOrderByKey: (key: TechnicalTypeSortBy) => void;
  view: EView;
  onSetView: (view: EView) => void;
}

const TechnicalTypeListFilterForm = ({
  query,
  setQuery,
  publishedFilter,
  setPublishedFilter,
  order,
  orderByKey,
  setOrder,
  setOrderByKey,
  view,
  onSetView,
}: Props) => {
  const { t } = useTranslation();

  const sortOptions = useMemo(
    () => [
      { label: t('technicalTypes.translatedName'), value: 'label' },
      { label: t('technicalTypes.nameLabel'), value: 'name' },
    ],
    [t],
  );

  return (
    <ListFilterToolbar
      popupId="technicalTypeFilters"
      searchQuery={query}
      onSearchChange={setQuery}
      searchPlaceholder={t('system.listFilters.searchPlaceholder')}
      hasActiveFilters={hasActiveSelectFilter(publishedFilter)}
      filterPopoverContent={
        <Grid container spacing={2} sx={{ p: 2 }}>
          <Grid size={12}>
            <FormControl fullWidth size="small">
              <InputLabel id="technical-type-published-filter-label" shrink>
                {t('system.sponsors.publishedStatus')}
              </InputLabel>
              <Select
                labelId="technical-type-published-filter-label"
                value={publishedFilter}
                label={t('system.sponsors.publishedStatus')}
                onChange={(e: SelectChangeEvent<PublishedFilter>) =>
                  setPublishedFilter(e.target.value as PublishedFilter)
                }
              >
                <MenuItem value="all">{t('base.all')}</MenuItem>
                <MenuItem value="true">{t('system.sponsors.published')}</MenuItem>
                <MenuItem value="false">{t('system.sponsors.unpublished')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      }
      order={order}
      orderByKey={orderByKey}
      setOrder={setOrder}
      setOrderByKey={key => setOrderByKey(key as TechnicalTypeSortBy)}
      sortOptions={sortOptions}
      view={view}
      onSetView={onSetView}
    />
  );
};

export default TechnicalTypeListFilterForm;
